/* @format */

import { api } from '@/scripts/api';

/**
 * @typedef {Object} GetCustomFieldsConfigBase
 * @property {string} type - Collection type of custom fields
 * @property {number} [accountId] - Account ID
 * @property {number} [clientId] - Client ID
 * @property {AbortSignal} [signal]
 */

/**
 * @typedef {GetCustomFieldsConfigBase} GetCustomFieldsConfig
 */

/**
 * @typedef {GetCustomFieldsConfigBase} GetCustomFieldValuesConfig
 * @property {Array} [where] - Optional where clauses to filter custom field values
 */

/**
 * @typedef {Object} MapCustomFieldHeaderConfig
 * @property {string|Function} [sortProperty] - Property to sort by
 * @property {boolean|Function} [sortable] - Indicate if the header is sortable or a function to determine if it is sortable.
 * @property {boolean|Function} [filterable] - Indicate if the header is filterable or a function to determine if it is filterable.
 * @property {boolean|Function} [editable] - Indicate if the header is editable or a function to determine if it is editable. *
 */

/**
 * Get the custom fields service endpoint based on type.
 *
 * @param type {string}
 * @param accountId {number} - Can either be a Client or Agency Account ID depending on the `type` provided.
 */
function getCustomFieldsEndpoint(type, accountId) {
    if (accountId == null) {
        throw new Error('Either accountId or clientId must be provided.');
    }

    switch (type) {
        case 'AgencyClient':
            return `/ms/custom-fields/v1/custom${type}Fields/account/${accountId}`; // Account ID
    }

    return `/ms/custom-fields/v1/custom${type}Fields/${accountId}`; // Client ID
}

/**
 * Get the custom field values service endpoint based on type.
 *
 * @param type
 * @param accountId {number} - Can either be a Client or Agency Account ID depending on the `type` provided.
 * @returns {string}
 */
function getCustomFieldValuesEndpoint(type, accountId) {
    if (accountId == null) {
        throw new Error('Either accountId or clientId must be provided.');
    }

    switch (type) {
        case 'AgencyClient':
            return `/ms/custom-fields/v1/custom${type}FieldValues/account/${accountId}`; // Account ID
    }

    return `/ms/custom-fields/v1/custom${type}FieldValues/${accountId}`; // Client ID
}

export const CustomFieldService = {
    /**
     * Create a custom company field for a client.
     *
     * @param {{
     *      clientId: number,
     *      name: string,
     *      description: string,
     *      inputType: string
     *  }} options
     */
    async createCompanyCustomField({ clientId, name, description, inputType }) {
        const url = getCustomFieldsEndpoint('Company', clientId);
        const payload = {
            Name: name,
            Description: description,
            InputType: inputType,
        };

        return api.post(url, payload);
    },

    /**
     * Create a custom contact field for a client.
     *
     * @param {{
     *      clientId: number,
     *      name: string,
     *      description: string,
     *      inputType: string
     *  }} options
     */
    async createContactCustomField({ clientId, name, description, inputType }) {
        const url = getCustomFieldsEndpoint('Contact', clientId);
        const payload = {
            Name: name,
            Description: description,
            InputType: inputType,
        };

        return api.post(url, payload);
    },

    /**
     * Get custom fields for a collection type. Either accountId or clientId must be provided.
     *
     * @param config {GetCustomFieldsConfig}
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async getCustomFields(config) {
        const endpoint = getCustomFieldsEndpoint(config.type, config.accountId ?? config.clientId);

        return api.ajax({
            method: 'GET',
            url: endpoint,
            signal: config.signal,
        });
    },

    /**
     * Get custom field values for a collection type. Either accountId or clientId must be provided.
     *
     * @param config {GetCustomFieldValuesConfig}
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async getCustomFieldValues(config) {
        config = {
            ...config,
            where: config.where ?? [],
        };

        let endpoint = getCustomFieldValuesEndpoint(
            config.type,
            config.accountId ?? config.clientId,
        );

        if (config.where?.length > 0) {
            endpoint += `?where=${JSON.stringify(config.where)}`;
        }

        return api.ajax({
            method: 'GET',
            url: endpoint,
            signal: config.signal,
        });
    },

    /**
     * Map the custom field record to a board header.
     *
     * @param customField {Object}
     * @param config {MapCustomFieldHeaderConfig}
     * @return {Object} - Custom field header object
     */
    mapCustomFieldHeader(customField, config) {
        const type = this.mapCustomFieldHeaderType(customField.InputType);
        const key = `${customField.Id}`;

        const sortProperty =
            typeof config.sortProperty === 'function'
                ? config.sortProperty(customField)
                : config.sortProperty;
        const sortable =
            typeof config.sortable === 'function' ? config.sortable(customField) : config.sortable;
        const filterable =
            typeof config.filterable === 'function'
                ? config.filterable(customField)
                : config.filterable;
        const editable =
            typeof config.editable === 'function' ? config.editable(customField) : config.editable;

        return {
            key,
            type,
            label: customField.Name,
            property: key,
            width: type === 'custom-field-multi-checkbox' ? 300 : 200,
            description: customField.Description,
            sortable,
            sortProperty,
            editable,
            filterable,
        };
    },

    getCustomFieldHeaders(type, clientIdOrAccountId) {
        let service = `/ms/custom-fields/v1/custom${type}Fields/${clientIdOrAccountId}`; // clientId

        if (type === 'AgencyClient') {
            service = `/ms/custom-fields/v1/custom${type}Fields/account/${clientIdOrAccountId}`; // accountId
        }

        return new Promise((resolve, reject) => {
            api.get(`${service}`).then(resolve).catch(reject);
        });
    },

    async getCustomFieldHeader(type, accountId, clientId, customFieldId) {
        let customFieldType;
        switch (type) {
            case 'agency-partner':
            case 'top-partner-report':
            case 'partner-qualified-leads-report':
            case 'mdf-management':
            case 'mdf-management-pending':
            case 'mdf-management-funds-allocated':
            case 'mdf-management-pop-submitted':
            case 'mdf-management-funds-pending-payment':
            case 'mdf-management-funds-in-transit':
            case 'mdf-management-funds-paid':
            case 'mdf-management-expired':
            case 'mdf-management-denied':
            case 'mdf-management-pop-denied':
            case 'mdf-report-management':
            case 'mdf-report-management-pending':
            case 'mdf-report-management-funds-allocated':
            case 'mdf-report-management-pop-submitted':
            case 'mdf-report-management-funds-pending-payment':
            case 'mdf-report-management-funds-in-transit':
            case 'mdf-report-management-funds-paid':
            case 'mdf-report-management-expired':
            case 'mdf-report-management-denied':
            case 'mdf-report-management-pop-denied':
            case 'mdf-preapproved-funds-request':
                customFieldType = 'AgencyClient';
                break;
            case 'contact':
            case 'contact-company':
            case 'contact-assigned':
            case 'leads':
            case 'lead':
            case 'list-details':
                customFieldType = 'Contact';
                break;
            case 'company':
            case 'all-accounts':
            case 'my-accounts':
            case 'unassigned-accounts':
                customFieldType = 'Company';
                break;
            case 'opportunity-contact':
            case 'opportunity':
                customFieldType = 'Opportunity';
                break;
            default:
                throw new Error('Unable to filter on custom field.');
        }

        let service = `/ms/custom-fields/v1/custom${customFieldType}Fields/${customFieldId}/${clientId}`;

        if (customFieldType === 'AgencyClient') {
            service = `/ms/custom-fields/v1/custom${customFieldType}Fields/${customFieldId}/account/${accountId}`;
        }

        return api.get(`${service}`);
    },

    getValueObject(type, value) {
        const valueObject = {
            name: 'ShortStringValue',
            value: value,
        };

        switch (type) {
            case 'single-line text':
                valueObject.name = 'ShortStringValue';
                break;
            case 'multi-line text':
                valueObject.name = 'LongStringValue';
                break;
            case 'date picker':
                valueObject.name = 'DateTimeValue';
                break;
            case 'single checkbox':
            case 'multiple checkboxes':
            case 'single dropdown':
                valueObject.name = 'ObjectValueJson';
                break;
            case 'number':
                valueObject.name = 'DoubleValue';
                valueObject.value = parseFloat(valueObject.value);
                break;
            case 'url':
                valueObject.name = 'ShortStringValue';
        }

        return valueObject;
    },

    mapCustomFieldHeaderType(type) {
        switch (type) {
            case 'single-line text':
                return 'custom-field-string';
            case 'multi-line text':
                return 'custom-field-multiline';
            case 'date picker':
                return 'custom-field-date';
            case 'single checkbox':
                return 'custom-field-single-checkbox';
            case 'multiple checkboxes':
                return 'custom-field-multi-checkbox';
            case 'number':
                return 'custom-field-number';
            case 'url':
                return 'custom-field-url';
            case 'single dropdown':
                return 'custom-field-single-dropdown';
        }
    },

    mapHeaderType(type) {
        switch (type) {
            case 'single-line text':
                return 'single';
            case 'multi-line text':
                return 'multi';
            case 'date picker':
                return 'date';
            case 'single checkbox':
                return 'check';
            case 'multiple checkboxes':
                return 'multicheck';
            case 'number':
                return 'number';
            case 'url':
                return 'url';
        }
    },

    async getCustomFieldListHeaders(clientId) {
        var customFieldHeaders = [];

        const types = ['Contact', 'Company'];

        try {
            await Promise.all(
                types.map((type) => {
                    return this.getCustomFieldHeaders(type, clientId).then((res) => {
                        const newHeaders = res.data.data;

                        newHeaders.forEach((header) => {
                            var customFieldType = this.mapHeaderType(header.InputType);

                            if (customFieldType !== 'check' && customFieldType !== 'multicheck') {
                                var customHeader = {
                                    name: header.Name,
                                    value: `customField-${type}-${customFieldType}-${header.Id}`,
                                };
                                customFieldHeaders.push(customHeader);
                            }
                        });
                    });
                }),
            );
        } catch (error) {
            this.$helpers.showFailNotification(
                'An error occurred while loading your custom field headers.',
                { error },
            );
        } finally {
            return customFieldHeaders;
        }
    },
};
